import {Button, ControlledToggle} from '@hm/fundamental'
import React, {useEffect, useState} from 'react'

import {useTranslation} from '../../../hooks/useTranslation'
import {prettyRoleName} from '../../../layouts/Roles/utils'
import {RoleAssignment} from '../../../modules/ManageUsers.selectors'

import {SelectGrouped, SelectGroupedOption} from './SelectGrouped'

type RolesFiltersProps = {
  isAllCollapsed: boolean
  setIsAllCollapsed: (collapsed: boolean) => void
  roles?: RoleAssignment[]
  setFilteredRoles: (roles: RoleAssignment[]) => void
}

type SelectedFilterProps = {
  roleType?: string
  country?: string
  isInternal?: string
  isAutogenerated?: string
}
export const RolesFilters = ({
  roles,
  setFilteredRoles,
  isAllCollapsed,
  setIsAllCollapsed
}: RolesFiltersProps) => {
  const {t} = useTranslation()
  const initFilterProps: SelectedFilterProps = {
    roleType: 'ALL',
    country: 'ALL',
    isInternal: 'ALL',
    isAutogenerated: 'ALL'
  }

  const [selectedFilters, setSelectedFilters] = useState<SelectedFilterProps>(initFilterProps)

  const countriesData = window.intlTelInputGlobals.getCountryData()

  const onlyExternalOrInternalRoles =
    roles?.every((role) => role.isInternal) || roles?.every((role) => !role.isInternal)

  const onlyAutogeneratedOrNot =
    roles?.every((role) => role.autoGenerated) || roles?.every((role) => !role.autoGenerated)

  const countryDataFilter = (countryId: string) => {
    if (countryId === 'MOCK') {
      return {label: 'MOCK', value: countryId}
    }
    if (countryId === '*') {
      return {label: 'All countries scope', value: countryId}
    }

    return {
      label:
        countriesData.find((countryData) => countryData.iso2.toUpperCase() === countryId)?.name ||
        '',
      value: countryId
    }
  }

  const roleOptions: SelectGroupedOption[] = [
    {label: t('rolesFilters.selectAll'), value: 'ALL'},
    ...Array.from(new Set(roles?.map(({roleType}) => roleType) || [])).map((roleType) => ({
      label: t(`roleAssignment.roleTypes.${roleType}`, prettyRoleName(roleType)),
      value: roleType
    }))
  ]

  const countryOptions: SelectGroupedOption[] = [
    {label: t('rolesFilters.selectAll'), value: 'ALL'},
    ...Array.from(new Set(roles?.map(({dataScope}) => dataScope['countryId'] as string) || [])).map(
      (countryId) => countryDataFilter(countryId)
    )
  ]

  const isInternalOptions: SelectGroupedOption[] = [
    {label: t('rolesFilters.selectAll'), value: 'ALL'},
    {label: t('rolesFilters.internalExternalFilter.isInternal'), value: 'true'},
    {label: t('rolesFilters.internalExternalFilter.isExternal'), value: 'false'}
  ]

  const isAutogeneratedOptions: SelectGroupedOption[] = [
    {label: t('rolesFilters.selectAll'), value: 'ALL'},
    {label: t('rolesFilters.autogeneratedFilter.autogenerated'), value: 'true'},
    {label: t('rolesFilters.autogeneratedFilter.notAutogenerated'), value: 'false'}
  ]

  useEffect(() => {
    if (!roles) {
      setFilteredRoles([])
      return
    }

    const filtered = roles.filter((role) => {
      if (
        selectedFilters.roleType &&
        selectedFilters.roleType !== 'ALL' &&
        role.roleType !== selectedFilters.roleType
      ) {
        return false
      }

      if (
        selectedFilters.country &&
        selectedFilters.country !== 'ALL' &&
        role.dataScope.countryId !== selectedFilters.country
      ) {
        return false
      }

      if (selectedFilters.isInternal && selectedFilters.isInternal !== 'ALL') {
        const isInternal = role.isInternal === true
        const filterIsInternal = selectedFilters.isInternal === 'true'
        if (isInternal !== filterIsInternal) {
          return false
        }
      }

      if (selectedFilters.isAutogenerated && selectedFilters.isAutogenerated !== 'ALL') {
        const isAutogenerated = role.autoGenerated === true
        const filterIsAutogenerated = selectedFilters.isAutogenerated === 'true'
        if (isAutogenerated !== filterIsAutogenerated) {
          return false
        }
      }

      return true
    })

    setFilteredRoles(filtered)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilters, roles])

  const filterDisplayConditions = [
    roleOptions.length > 2,
    countryOptions.length > 2,
    !onlyExternalOrInternalRoles,
    !onlyAutogeneratedOrNot
  ]
  const isAnyFilterVisible = filterDisplayConditions.some((condition) => condition)

  return (
    <div className="flex flex-col gap-4 shadow-md p-2 rounded-md lg:shadow-none lg:rounded-none lg:p-0">
      <div className="flex flex-col gap-2 items-center lg:flex-row  lg:justify-between">
        {filterDisplayConditions[0] && (
          <SelectGrouped
            data-test-id="role-type-filter"
            inputLabel={t('rolesFilters.roleTypeFilter.selectRoleTypeLabel')}
            searchPlaceholder={t('rolesFilters.roleTypeFilter.selectRoleTypePlaceholder')}
            value={selectedFilters.roleType || ''}
            onChange={(value) => setSelectedFilters((prev) => ({...prev, roleType: value}))}
            options={roleOptions}
            showSearch={true}
          />
        )}
        {filterDisplayConditions[1] && (
          <SelectGrouped
            data-test-id="country-filter"
            inputLabel={t('rolesFilters.countryFilter.selectCountriesLabel')}
            searchPlaceholder={t('rolesFilters.countryFilter.selectCountriesPlaceholder')}
            value={selectedFilters.country || ''}
            onChange={(value) => setSelectedFilters((prev) => ({...prev, country: value}))}
            options={countryOptions}
            showSearch={true}
          />
        )}
        {filterDisplayConditions[2] && (
          <SelectGrouped
            data-test-id="internal-external-filter"
            inputLabel={t('rolesFilters.internalExternalFilter.selectInternalExternalLabel')}
            value={selectedFilters.isInternal || ''}
            onChange={(value) => setSelectedFilters((prev) => ({...prev, isInternal: value}))}
            options={isInternalOptions}
          />
        )}
        {filterDisplayConditions[3] && (
          <SelectGrouped
            data-test-id="autogenerated-filter"
            inputLabel={t('rolesFilters.autogeneratedFilter.selectAutogeneratedLabel')}
            value={selectedFilters.isAutogenerated || ''}
            onChange={(value) => setSelectedFilters((prev) => ({...prev, isAutogenerated: value}))}
            options={isAutogeneratedOptions}
          />
        )}
        {isAnyFilterVisible && (
          <Button
            variant="tertiary"
            onClick={() => setSelectedFilters(initFilterProps)}
            data-cy="button-clear-all-filters"
            className="self-end lg:self-center"
          >
            {t('rolesFilters.clearAllFilters')}
          </Button>
        )}
      </div>
      <div className="self-end px-4">
        <ControlledToggle
          checked={isAllCollapsed}
          name="collapse-toggle"
          onChange={() => setIsAllCollapsed(!isAllCollapsed)}
          label={t('rolesFilters.collapseAll')}
          data-cy="collapse-all-roles-toggle"
        />
      </div>
    </div>
  )
}
